import { ApiTokensCard } from './components/ApiTokensCard';
import { hasPermission, type AccessPolicy } from '../../api/src/auth/permissions';
import { mergeAccessUser } from './access-policy';
import { Icon } from '@iconify/react';
import { Alert, Button, CircularProgress, Container } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { lazy, Suspense, useEffect, useRef, useState, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import type { UserRole } from '../../api/src/auth/access-role';
import { GOOGLE_STATE_ERROR } from '../../api/src/auth/google-auth-contract';
import { api, ApiError } from './api/client';
import { SECTION_CATALOG, sectionById, type SectionId } from './api/generated/section-catalog';
import type { AuthConfig, User } from './api/types';
import { DashboardShell } from './components/DashboardShell';
import { Box, Stack, Typography } from './components/mui-system';
import { SectionRefreshNotice } from './components/SectionRefreshNotice';
import { WorkspaceOnboarding } from './components/WorkspaceOnboarding';
import { AdminPage } from './pages/AdminPage';
import { AlertsPage } from './pages/AlertsPage';
import { DonorsPage } from './pages/DonorsPage';
import { FakesPage } from './pages/FakesPage';
import { MetricsPage } from './pages/MetricsPage';
import { PositionsPage } from './pages/PositionsPage';
import { RegistryPage } from './pages/RegistryPage';
import { ReportsPage } from './pages/ReportsPage';
import { SettingsPage } from './pages/SettingsPage';
import { JobsPage } from './pages/JobsPage';
import { ShortsPage } from './pages/ShortsPage';
import { TelegramPage } from './pages/TelegramPage';
import { SectionProvider, useSections } from './section-context';
import { openAuth } from './telegram';
import { AppThemeProvider, ThemeModeButton } from './theme-mode';
import { useWorkspace, WorkspaceProvider } from './workspace-context';

const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));

const sectionPages = {
  alerts: AlertsPage,
  fakes: FakesPage,
  jobs: JobsPage,
  donors: DonorsPage,
  positions: PositionsPage,
  metrics: MetricsPage,
  reports: ReportsPage,
  telegram: TelegramPage,
  shorts: ShortsPage,
  settings: SettingsPage,
  registry: RegistryPage,
  documentation: DocumentationPage,
} satisfies Record<SectionId, ComponentType>;

export function App() {
  return (
    <SectionProvider>
      <Application />
    </SectionProvider>
  );
}

function Application() {
  const sectionSettings = useSections();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const authStarted = useRef(false);

  useEffect(() => {
    if (authStarted.current) return;
    authStarted.current = true;
    Promise.all([
      api.authConfig(),
      api.me().catch((caught) => {
        if (caught instanceof ApiError && caught.status === 401) return null;
        throw caught;
      }),
    ])
      .then(async ([authConfig, session]) => {
        const activeSession = session ?? (authConfig.devAuthEnabled ? await api.devLogin() : null);
        setConfig(authConfig);
        setUser(activeSession?.user ?? null);
      })
      .catch((caught) => setError(caught instanceof Error ? caught.message : 'API недоступен'))
      .finally(() => setLoading(false));
  }, []);

  async function refreshUser() {
    const observedGrantId = user?.access.grantId;
    const next = (await api.me()).user;
    setUser((current) => mergeAccessUser(current, next, observedGrantId));
  }

  const userId = user?.id;
  const grantId = user?.access.grantId;
  useEffect(() => {
    if (!userId) return;
    let active = true;
    let refreshing = false;
    const roleChanged = (event: Event) => {
      const value = (event as CustomEvent<{ userId: string; role: UserRole; access: AccessPolicy }>)
        .detail;
      if (value.userId === userId && value.access.grantId !== grantId) {
        refresh();
        return;
      }
      setUser((current) =>
        current && current.id === value.userId && current.access.grantId === value.access.grantId
          ? mergeAccessUser(current, {
              ...current,
              role: value.role,
              access: value.access,
              isAdmin: value.role === 'superadmin',
            })
          : current,
      );
    };
    const refresh = () => {
      if (refreshing) return;
      refreshing = true;
      void api
        .me()
        .then(({ user: refreshed }) => {
          if (active) setUser((current) => mergeAccessUser(current, refreshed, grantId));
        })
        .catch((caught) => {
          if (active && caught instanceof ApiError && caught.status === 401) setUser(null);
        })
        .finally(() => {
          refreshing = false;
        });
    };
    window.addEventListener('user-access', roleChanged);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      window.removeEventListener('user-access', roleChanged);
      window.removeEventListener('focus', refresh);
    };
  }, [userId, grantId]);

  async function logout() {
    await api.logout();
    setUser(null);
  }

  return <AppThemeProvider userId={user?.id}>{renderScreen()}</AppThemeProvider>;

  function renderScreen() {
    if (loading || sectionSettings.loading) return <LoadingScreen />;
    if (error) return <ServiceError message={error} />;
    if (sectionSettings.error && sectionSettings.sections.length === 0)
      return <ServiceError message={sectionSettings.error} onRetry={sectionSettings.refresh} />;
    if (!user) return <LoginScreen config={config!} />;
    return (
      <WorkspaceProvider
        key={`${user.id}:${user.access.grantId}:${user.access.revision}:${user.role}`}
        user={user}
        refreshUser={refreshUser}
      >
        <BrowserRouter>
          <Routes>
            <Route element={<DashboardShell onLogout={logout} />}>
              <Route
                path="/admin/:tab?"
                element={user.isAdmin ? <AdminPage /> : <Navigate to="/" replace />}
              />
              <Route path="/account" element={<ApiTokensCard />} />
              <Route path="/access" element={<Navigate to="/admin/users" replace />} />
              <Route path="/fakes/:keywordId?" element={<LegacySearchMapRedirect />} />
              <Route element={<SectionGate />}>
                <Route
                  path={`${SECTION_CATALOG.find(({ id }) => id === 'documentation')!.path}/*`}
                  element={
                    <Suspense fallback={<CircularProgress aria-label="Загрузка документации" />}>
                      <DocumentationPage />
                    </Suspense>
                  }
                />
                <Route element={<WorkspaceGate />}>
                  {SECTION_CATALOG.filter(({ id }) => id !== 'documentation').map(
                    ({ id, path }) => {
                      const Page = sectionPages[id];
                      return (
                        <Route
                          key={id}
                          path={id === 'fakes' ? `${path}/:keywordId?` : path}
                          element={<Page />}
                        />
                      );
                    },
                  )}
                </Route>
              </Route>
              <Route path="/" element={<DefaultSection />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </WorkspaceProvider>
    );
  }
}

function LegacySearchMapRedirect() {
  const { pathname, search, hash, state } = useLocation();
  return (
    <Navigate
      replace
      state={state}
      to={{
        pathname: sectionById('fakes').path + pathname.slice('/fakes'.length),
        search,
        hash,
      }}
    />
  );
}

function SectionGate() {
  const { user } = useWorkspace();
  const { pathname } = useLocation();
  const { visibleSections } = useSections();
  const section = SECTION_CATALOG.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`),
  );
  if (
    !section ||
    !visibleSections.some(({ id }) => id === section.id) ||
    !hasPermission(user, `${section.id}.view`)
  )
    return <Navigate to="/" replace />;
  return <Outlet />;
}

function DefaultSection() {
  const { user } = useWorkspace();
  const { visibleSections: visible } = useSections();
  const visibleSections = visible.filter((section) => hasPermission(user, `${section.id}.view`));
  if (visibleSections[0]) return <Navigate to={visibleSections[0].path} replace />;
  return <Alert severity="info">Нет доступных разделов. Обратитесь к администратору.</Alert>;
}

function WorkspaceGate() {
  const { activeWorkspace, loading, error, user } = useWorkspace();
  if (loading) return <LoadingScreen />;
  if (error) return <ServiceError message={error} />;
  if (!activeWorkspace) return <WorkspaceOnboarding />;
  return <Outlet key={`${activeWorkspace.id}:${user.role}`} />;
}

function LoadingScreen() {
  return (
    <Box minHeight="100vh" display="grid" sx={{ placeItems: 'center' }}>
      <CircularProgress color="primary" />
    </Box>
  );
}

function ServiceError({ message, onRetry }: { message: string; onRetry?: () => Promise<void> }) {
  return (
    <Container maxWidth="sm" sx={{ py: 10 }}>
      <Alert severity="error">
        <b>SEO.Dashboard API недоступен.</b>
        <br />
        {message}
      </Alert>
      {onRetry && (
        <Button onClick={() => void onRetry()} sx={{ mt: 2 }}>
          Повторить загрузку
        </Button>
      )}
    </Container>
  );
}

function LoginScreen({ config }: { config: AuthConfig }) {
  const { visibleSections } = useSections();
  const loginLocation = new URL(window.location.href);
  const invalidGoogleState = loginLocation.searchParams.get('authError') === GOOGLE_STATE_ERROR;
  loginLocation.searchParams.delete('authError');
  const returnTo = `${loginLocation.pathname}${loginLocation.search}`;
  const loginUrl = `/api/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`;
  return (
    <Box minHeight="100vh" display="grid" sx={{ placeItems: 'center', p: 2.5 }}>
      <Box sx={{ position: 'fixed', top: 14, right: 14, zIndex: 2 }}>
        <ThemeModeButton />
      </Box>
      <Container maxWidth="lg">
        <SectionRefreshNotice />
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', md: '1.2fr .8fr' }}
          gap={{ xs: 4, md: 7 }}
          alignItems="center"
        >
          <Box>
            <Box component="img" src="/logo.svg" alt="" width={54} height={54} mb={3} />
            <Typography variant="h1" maxWidth={720}>
              Всё, что двигает органический рост.
            </Typography>
            <Typography color="text.secondary" fontSize={{ xs: 17, md: 20 }} maxWidth={610} mt={2}>
              Инструменты для органического роста в едином рабочем пространстве SEO-команды.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.8} mt={3}>
              {visibleSections.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    px: 1.4,
                    py: 0.65,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '7px',
                    bgcolor: 'action.hover',
                    fontWeight: 'fontWeightBold',
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Stack>
          </Box>
          <Box
            sx={(theme: Theme) => ({
              bgcolor: '#15161c',
              color: '#f4f2ed',
              border: '1px solid rgba(255,255,255,.14)',
              borderRadius: '12px',
              p: { xs: 3, sm: 4 },
              boxShadow: '0 28px 80px rgba(0,0,0,.28)',
              '& .MuiButton-root.Mui-disabled': {
                color: 'rgba(244,242,237,.34)',
                bgcolor: 'rgba(255,255,255,.09)',
              },
              ...(theme.colorTheme === 'graphite'
                ? {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderColor: 'divider',
                    boxShadow: 'none',
                  }
                : {}),
            })}
          >
            <Typography variant="overline" sx={{ color: 'secondary.main', letterSpacing: '.16em' }}>
              SECURE ACCESS
            </Typography>
            <Typography variant="h2" mt={1} mb={1.5}>
              Вход в систему
            </Typography>
            <Typography sx={{ color: 'rgba(244,242,237,.6)' }} mb={3}>
              Используйте рабочий Google-аккаунт. Пароли в SEO.Dashboard не хранятся.
            </Typography>
            {invalidGoogleState && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Попытка входа устарела или уже использована. Нажмите «Продолжить с Google», чтобы
                начать заново.
              </Alert>
            )}
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="primary"
              disabled={!config.googleConfigured}
              onClick={() => openAuth(loginUrl)}
              startIcon={<Icon icon="logos:google-icon" />}
              sx={{ py: 1 }}
            >
              Продолжить с Google
            </Button>
            {!config.googleConfigured && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Google OAuth ещё не настроен. Redirect URI: <b>{config.redirectUri}</b>
              </Alert>
            )}
            {config.allowedDomains.length > 0 && (
              <Typography fontSize={12} mt={1.5} sx={{ color: 'rgba(244,242,237,.48)' }}>
                Разрешённые домены: {config.allowedDomains.join(', ')}
              </Typography>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
